// A small example pack so a first-time visitor can press "Load example" and
// immediately have something playable. Purely client-side seed data — nothing
// here is baked into a room until the user creates one.

import type { Pack } from "./pack";
import { PACK_VERSION } from "./pack";
import type { Language } from "./types";

const EN: Pack = {
  version: PACK_VERSION,
  title: "Example Quiz",
  rounds: [
    {
      name: "Round 1",
      categories: [
        {
          category: "World Capitals",
          questions: [
            { points: 100, q: "Capital of Japan", a: "Tokyo", image: null },
            { points: 200, q: "Capital of Canada", a: "Ottawa", image: null },
            { points: 300, q: "Capital of Australia", a: "Canberra", image: null },
            { points: 400, q: "Capital of Morocco", a: "Rabat", image: null },
            { points: 500, q: "Capital of Kazakhstan", a: "Astana", image: null },
          ],
        },
        {
          category: "Science",
          questions: [
            { points: 100, q: "Chemical symbol for gold", a: "Au", image: null },
            { points: 200, q: "Planet known as the Red Planet", a: "Mars", image: null },
            { points: 300, q: "Gas plants absorb for photosynthesis", a: "Carbon dioxide", image: null },
            { points: 400, q: "Number of bones in an adult human body", a: "206", image: null },
            { points: 500, q: "Scientist who proposed general relativity", a: "Albert Einstein", image: null },
          ],
        },
        {
          category: "Movies",
          questions: [
            { points: 100, q: "Director of Jaws and E.T.", a: "Steven Spielberg", image: null },
            { points: 200, q: "Film featuring a character named Ellen Ripley", a: "Alien", image: null },
            { points: 300, q: "First feature-length computer-animated film", a: "Toy Story", image: null },
            { points: 400, q: "Studio behind Spirited Away", a: "Studio Ghibli", image: null },
            { points: 500, q: "Year the first Star Wars film was released", a: "1977", image: null },
          ],
        },
      ],
    },
    {
      name: "Round 2 — Double",
      categories: [
        {
          category: "History",
          questions: [
            { points: 200, q: "Year the Berlin Wall fell", a: "1989", image: null },
            { points: 400, q: "Empire ruled by Genghis Khan", a: "The Mongol Empire", image: null },
            { points: 600, q: "Ship that carried the Pilgrims in 1620", a: "The Mayflower", image: null },
            { points: 800, q: "Ancient wonder that stood in Alexandria", a: "The Lighthouse (Pharos)", image: null },
            { points: 1000, q: "Treaty that ended the First World War", a: "The Treaty of Versailles", image: null },
          ],
        },
        {
          category: "Music",
          questions: [
            { points: 200, q: "Number of strings on a standard guitar", a: "Six", image: null },
            { points: 400, q: "Composer of the Ninth Symphony ('Ode to Joy')", a: "Ludwig van Beethoven", image: null },
            { points: 600, q: "Band that recorded Abbey Road", a: "The Beatles", image: null },
            { points: 800, q: "Instrument Miles Davis played", a: "The trumpet", image: null },
            { points: 1000, q: "Italian term meaning 'gradually louder'", a: "Crescendo", image: null },
          ],
        },
        {
          category: "Food",
          questions: [
            { points: 200, q: "Main ingredient in guacamole", a: "Avocado", image: null },
            { points: 400, q: "Country where sushi originated", a: "Japan", image: null },
            { points: 600, q: "Spice that gives curry its yellow colour", a: "Turmeric", image: null },
            { points: 800, q: "Cheese traditionally used on pizza margherita", a: "Mozzarella", image: null },
            { points: 1000, q: "Bean that chocolate is made from", a: "The cacao bean", image: null },
          ],
        },
      ],
    },
  ],
  wager: {
    name: "Final Round",
    question: "Which country has the most time zones?",
    answer: "France (12, counting overseas territories)",
    image: null,
  },
};

const DE: Pack = {
  version: PACK_VERSION,
  title: "Beispiel-Quiz",
  rounds: [
    {
      name: "Runde 1",
      categories: [
        {
          category: "Hauptstädte",
          questions: [
            { points: 100, q: "Hauptstadt von Japan", a: "Tokio", image: null },
            { points: 200, q: "Hauptstadt von Kanada", a: "Ottawa", image: null },
            { points: 300, q: "Hauptstadt von Australien", a: "Canberra", image: null },
            { points: 400, q: "Hauptstadt von Marokko", a: "Rabat", image: null },
            { points: 500, q: "Hauptstadt von Kasachstan", a: "Astana", image: null },
          ],
        },
        {
          category: "Wissenschaft",
          questions: [
            { points: 100, q: "Chemisches Symbol für Gold", a: "Au", image: null },
            { points: 200, q: "Welcher Planet heißt der Rote Planet?", a: "Mars", image: null },
            { points: 300, q: "Welches Gas nehmen Pflanzen zur Fotosynthese auf?", a: "Kohlendioxid", image: null },
            { points: 400, q: "Wie viele Knochen hat ein erwachsener Mensch?", a: "206", image: null },
            { points: 500, q: "Wer formulierte die allgemeine Relativitätstheorie?", a: "Albert Einstein", image: null },
          ],
        },
        {
          category: "Filme",
          questions: [
            { points: 100, q: "Regisseur von Der weiße Hai und E.T.", a: "Steven Spielberg", image: null },
            { points: 200, q: "In welchem Film kommt Ellen Ripley vor?", a: "Alien", image: null },
            { points: 300, q: "Erster abendfüllende Computeranimationsfilm", a: "Toy Story", image: null },
            { points: 400, q: "Welches Studio produzierte Chihiros Reise ins Zauberland?", a: "Studio Ghibli", image: null },
            { points: 500, q: "In welchem Jahr kam der erste Star-Wars-Film heraus?", a: "1977", image: null },
          ],
        },
      ],
    },
    {
      name: "Runde 2 — Doppelt",
      categories: [
        {
          category: "Geschichte",
          questions: [
            { points: 200, q: "In welchem Jahr fiel die Berliner Mauer?", a: "1989", image: null },
            { points: 400, q: "Welches Reich beherrschte Dschingis Khan?", a: "Das Mongolische Reich", image: null },
            { points: 600, q: "Wie hieß das Schiff der Pilgerväter von 1620?", a: "Mayflower", image: null },
            { points: 800, q: "Welches Weltwunder stand in Alexandria?", a: "Der Leuchtturm (Pharos)", image: null },
            { points: 1000, q: "Welcher Vertrag beendete den Ersten Weltkrieg?", a: "Der Vertrag von Versailles", image: null },
          ],
        },
        {
          category: "Musik",
          questions: [
            { points: 200, q: "Wie viele Saiten hat eine normale Gitarre?", a: "Sechs", image: null },
            { points: 400, q: "Wer komponierte die Neunte Sinfonie („Ode an die Freude“)?", a: "Ludwig van Beethoven", image: null },
            { points: 600, q: "Welche Band nahm Abbey Road auf?", a: "Die Beatles", image: null },
            { points: 800, q: "Welches Instrument spielte Miles Davis?", a: "Die Trompete", image: null },
            { points: 1000, q: "Welcher italienische Begriff bedeutet „allmählich lauter“?", a: "Crescendo", image: null },
          ],
        },
        {
          category: "Essen",
          questions: [
            { points: 200, q: "Hauptzutat von Guacamole", a: "Avocado", image: null },
            { points: 400, q: "In welchem Land entstand Sushi?", a: "Japan", image: null },
            { points: 600, q: "Welches Gewürz färbt Curry gelb?", a: "Kurkuma", image: null },
            { points: 800, q: "Welcher Käse gehört auf eine Pizza Margherita?", a: "Mozzarella", image: null },
            { points: 1000, q: "Aus welcher Bohne wird Schokolade gemacht?", a: "Der Kakaobohne", image: null },
          ],
        },
      ],
    },
  ],
  wager: {
    name: "Finalrunde",
    question: "Welches Land hat die meisten Zeitzonen?",
    answer: "Frankreich (12, mit den Überseegebieten)",
    image: null,
  },
};

export function samplePack(lang: Language): Pack {
  // Structured clone so the editor can mutate its copy freely.
  return JSON.parse(JSON.stringify(lang === "de" ? DE : EN)) as Pack;
}
